<template>
  <el-dialog :title="'彻查作业 - ' + selectedStudentName" :visible.sync="localVisible" class="student-history-dialog" @close="handleClose">
    <div slot="title">
      <span style="font-weight: bold; font-size: 18px;">彻查作业 - {{ selectedStudentName }}</span>
      <el-button size="mini" type="primary" @click="exportStudentData" style="margin-left: 15px;">导出数据</el-button>
    </div>
    
    <!-- Loading状态 -->
    <div v-if="!showNoRecords && records.length === 0" style="text-align: center; padding: 50px;">
      <div style="display: flex; flex-direction: column; align-items: center; justify-content: center;">
        <i class="el-icon-loading" style="font-size: 30px; margin-bottom: 20px; animation: rotating 2s linear infinite;"></i>
        <p style="font-size: 16px; color: #606266;">正在加载{{ subjectName }}作业记录...</p>
      </div>
    </div>
    
    <!-- 无记录状态 -->
    <div v-else-if="showNoRecords && records.length === 0" style="text-align: center; padding: 50px;">
      <div style="display: flex; flex-direction: column; align-items: center; justify-content: center;">
        <i class="el-icon-success" style="font-size: 60px; color: #67c23a; margin-bottom: 20px; text-shadow: 0 2px 8px rgba(103, 194, 58, 0.3);"></i>
        <p style="font-size: 18px; font-weight: bold; color: #67c23a; margin: 0; white-space: nowrap;">该学生所有{{ subjectName }}作业已完成！</p>
        <p style="font-size: 14px; color: #909399; margin-top: 10px;">🎉 太棒了！继续保持</p>
      </div>
    </div>
    
    <!-- 数据表格 -->
    <div v-else>
      <el-table :data="records" style="width: 100%" :row-class-name="tableRowClassName" @sort-change="handleSortChange" ref="studentHistoryTable">
      <el-table-column prop="date" label="日期" width="85" sortable="custom">
        <template slot-scope="scope">
          <el-button 
            size="mini" 
            :type="scope.row.markedAsFinished ? 'info' : 'danger'"
            @click="markAsFinished(scope.row)"
            :key="scope.row.id + '-' + scope.row.markedAsFinished"
            style="width: 100%; font-size: 14px; padding: 7px 5px; box-shadow: 0 2px 4px rgba(0,0,0,0.2);">
            {{ formatDateDisplay(scope.row.date) }}
          </el-button>
        </template>
      </el-table-column>
      <el-table-column prop="typeName" label="作业类型" width="100" sortable="custom">
        <template slot-scope="scope">
          <el-tooltip :content="scope.row.typeName" placement="top" effect="light" popper-class="white-tooltip" :hide-after="1000">
            <span :class="{ 'marked-as-finished': !!scope.row.markedAsFinished }" style="display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; cursor: pointer;">{{ scope.row.typeName }}</span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column prop="content" label="内容" min-width="150" show-overflow-tooltip>
        <template slot-scope="scope">
          <span :class="{ 'marked-as-finished': !!scope.row.markedAsFinished }">{{ scope.row.content }}</span>
        </template>
      </el-table-column>
      <el-table-column label="功能" width="150">
        <template slot-scope="scope">
          <div style="display: flex; gap: 5px;">
            <el-button 
              size="mini" 
              type="primary" 
              @click="editHomeworkContent(scope.row)"
              :disabled="!!scope.row.markedAsFinished">
              修改内容
            </el-button>
            <el-button 
              size="mini" 
              type="success" 
              @click="locateToRecord(scope.row)"
              :disabled="!!scope.row.markedAsFinished">
              定位
            </el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: 'MobileStudentHistoryDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    selectedStudentName: {
      type: String,
      default: ''
    },
    records: {
      type: Array,
      default: () => []
    },
    sortField: {
      type: String,
      default: 'typeName'
    },
    sortOrder: {
      type: String,
      default: 'asc'
    },
    subjectName: {
      type: String,
      default: ''
    },
    showNoRecords: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      lastSort: {
        field: 'typeName',
        order: 'asc'
      }
    };
  },
  computed: {
    localVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit('update:visible', value);
      }
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        // 对话框打开时，设置表格默认排序状态
        this.$nextTick(() => {
          if (this.$refs.studentHistoryTable) {
            const sortOrder = this.sortOrder === 'asc' ? 'ascending' : 'descending';
            this.lastSort = {
              field: this.sortField,
              order: sortOrder
            };
            this.$refs.studentHistoryTable.sort(this.sortField, sortOrder);
          }
        });
        
        // 添加滚动事件监听
        window.addEventListener('scroll', this.handleScroll, true);
      } else {
        // 对话框关闭时，移除滚动事件监听
        window.removeEventListener('scroll', this.handleScroll, true);
      }
    }
  },
  mounted() {
    // 添加滚动事件监听
    window.addEventListener('scroll', this.handleScroll, true);
  },
  beforeDestroy() {
    // 移除滚动事件监听
    window.removeEventListener('scroll', this.handleScroll, true);
  },
  methods: {
    handleScroll() {
      const tooltips = document.querySelectorAll('.el-tooltip__popper');
      tooltips.forEach(tooltip => {
        tooltip.style.display = 'none';
      });
    },
    handleClose() {
      this.$emit('update:visible', false);
    },
    tableRowClassName({row, rowIndex}) {
      // 为学生历史欠交记录表格添加行样式
      if (this.records && this.records.length > 0) {
        // 根据当前排序字段确定分组键
        let groupKey;
        if (this.sortField === 'date') {
          groupKey = row.date;
        } else if (this.sortField === 'typeName') {
          groupKey = row.typeName;
        }
        
        // 如果有分组键，则为相同分组的行添加相同的背景色类
        if (groupKey !== undefined) {
          // 计算该分组键在所有分组中的索引，用于确定背景色
          const uniqueGroups = [];
          for (let i = 0; i < this.records.length; i++) {
            let key;
            if (this.sortField === 'date') {
              key = this.records[i].date;
            } else if (this.sortField === 'typeName') {
              key = this.records[i].typeName;
            }
            
            if (!uniqueGroups.includes(key)) {
              uniqueGroups.push(key);
            }
          }
          
          const groupIndex = uniqueGroups.indexOf(groupKey);
          // 使用不同的背景色类
          if (groupIndex % 2 === 0) {
            return 'group-row-light';
          } else {
            return 'group-row-dark';
          }
        }
      }
      return '';
    },
    handleSortChange({ column, prop, order }) {
      // 如果排序为null，则使用与上次相反的排序
      if (order === null) {
        // 设置为与上次相反的排序
        order = this.lastSort.order === 'descending' ? 'ascending' : 'descending';
      }
      
      // 无论什么情况，都要确保只有一个列处于排序状态
      if (this.$refs.studentHistoryTable) {
        const columns = this.$refs.studentHistoryTable.columns;
        columns.forEach(col => {
          if (col.property !== prop) {
            col.order = null;
          }
        });
        
        // 更新当前列的排序状态
        const currentColumn = columns.find(col => col.property === prop);
        if (currentColumn) {
          currentColumn.order = order;
        }
      }
      
      // 保存当前排序状态
      this.lastSort = {
        field: prop,
        order: order
      };
      
      // 通知父组件排序变化
      this.$emit('sort', { column, prop, order });
    },
    markAsFinished(row) {
      this.$emit('mark', row);
    },
    editHomeworkContent(row) {
      this.$emit('edit', row);
    },
    locateToRecord(row) {
      this.$emit('locate', row);
    },
    exportStudentData() {
      this.$emit('export');
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    formatDateDisplay(dateString) {
      const date = new Date(dateString);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${month}-${day}`;
    }
  }
};
</script>

<style scoped>
@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* 学生历史欠交记录弹窗样式 */
.student-history-dialog ::v-deep .el-dialog {
  margin: 10px !important;
  margin-top: 15vh !important;
  width: calc(100% - 20px) !important;
}

/* 移动端弹窗body padding调整 */
.student-history-dialog ::v-deep .el-dialog__body {
  padding: 10px !important;
}

/* 白色tooltip样式 */
.white-tooltip {
  background-color: #fff !important;
  color: #333 !important;
  border: 1px solid #e4e7ed !important;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1) !important;
}

/* 表格样式优化 */
.el-table {
  border-radius: 10px;
  overflow: hidden;
  /* 允许在表格中选择文字 */
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
  user-select: text;
}

.el-table th {
  background-color: #ecf5ff;
}

/* 语文主题表格头部 */
body.chinese-theme .el-table th {
  background-color: #fffacd;
}

/* 数学主题表格头部 */
body.math-theme .el-table th {
  background-color: #e0ffff;
}

/* 英语主题表格头部 */
body.english-theme .el-table th {
  background-color: #ffe4e1;
}

/* 按钮样式优化 */
.el-button {
  border-radius: 10px;
  font-weight: bold;
}

.el-button--primary:not(:disabled) {
  background: linear-gradient(90deg, #409EFF, #64b5f6);
  border: none;
}

/* 语文主题主要按钮 */
body.chinese-theme .el-button--primary:not(:disabled) {
  background: linear-gradient(90deg, #d2691e, #ff8c00);
}

/* 数学主题主要按钮 */
body.math-theme .el-button--primary:not(:disabled) {
  background: linear-gradient(90deg, #1e90ff, #4169e1);
}

/* 英语主题主要按钮 */
body.english-theme .el-button--primary:not(:disabled) {
  background: linear-gradient(90deg, #ff6b6b, #ff1493);
}

.el-button--success:not(:disabled) {
  background: linear-gradient(90deg, #67C23A, #95d475);
  border: none;
}

.el-button--warning:not(:disabled) {
  background: linear-gradient(90deg, #E6A23C, #f3d19e);
  border: none;
}

.el-button--danger:not(:disabled) {
  background: linear-gradient(90deg, #F56C6C, #ff8e8e);
  border: none;
}

/* 修复标记完成按钮悬浮时闪烁白色的问题 */
.el-button--danger:not(:disabled):hover {
  background: linear-gradient(90deg, #ff6b6b, #ff7b7b) !important;
  border: none !important;
  color: white !important;
}

/* 表格中的按钮样式优化 */
.el-table .el-button--danger:not(:disabled) {
  background: linear-gradient(90deg, #F56C6C, #ff8e8e) !important;
  border: none !important;
  color: white !important;
}

.el-table .el-button--primary:not(:disabled) {
  background: linear-gradient(90deg, #409EFF, #64b5f6) !important;
  border: none !important;
  color: white !important;
}

.el-table .el-button--success:not(:disabled) {
  background: linear-gradient(90deg, #67C23A, #95d475) !important;
  border: none !important;
  color: white !important;
}

/* 主题按钮样式 */
body.chinese-theme .el-table .el-button--primary:not(:disabled) {
  background: linear-gradient(90deg, #d2691e, #ff8c00) !important;
}

body.math-theme .el-table .el-button--primary:not(:disabled) {
  background: linear-gradient(90deg, #1e90ff, #4169e1) !important;
}

body.english-theme .el-table .el-button--primary:not(:disabled) {
  background: linear-gradient(90deg, #ff6b6b, #ff1493) !important;
}

/* 表格按钮悬停效果 */
.el-table .el-button--danger:not(:disabled):hover {
  background: linear-gradient(90deg, #ff6b6b, #ff7b7b) !important;
  border: none !important;
  color: white !important;
  transform: scale(1.05);
  transition: all 0.2s ease;
}

.el-table .el-button--success:not(:disabled):hover {
  background: linear-gradient(90deg, #5cb831, #7cd94d) !important;
  border: none !important;
  color: white !important;
  transform: scale(1.05);
  transition: all 0.2s ease;
}

/* 已标记完成行的样式 */
.marked-as-finished {
  text-decoration: line-through;
  color: #C0C4CC;
}

/* 提高禁用按钮样式的优先级，防止被其他样式覆盖 */
.el-table .el-button:disabled,
.el-table .el-button:disabled:hover,
.el-table .el-button:disabled:focus,
.el-table .el-button[disabled],
.el-table .el-button[disabled]:hover,
.el-table .el-button[disabled]:focus {
  background-color: #DCDFE6 !important;
  border-color: #DCDFE6 !important;
  color: #C0C4CC !important;
  cursor: not-allowed !important;
  opacity: 1 !important;
  transform: none !important;
  box-shadow: none !important;
}

/* 表格行背景色样式 */
.group-row-light {
  background-color: #f0f9eb !important;
}

.group-row-dark {
  background-color: #e0e0e0 !important;
}

/* 禁用表格行hover效果 */
.el-table--enable-row-hover .el-table__body tr:hover > td {
  background-color: transparent !important;
}

/* 弹窗中表格的特殊样式 */
.student-history-dialog .el-table .el-button--danger:not(:disabled),
.unfinished-students-dialog .el-table .el-button--danger:not(:disabled) {
  background: linear-gradient(90deg, #F56C6C, #ff8e8e) !important;
  border: none !important;
  color: white !important;
}
</style>